/**
 * LaTeX → Unicode text.
 *
 * Reads LaTeX that is mostly symbols — Greek letters, operators, super- and subscripts,
 * `\mathbb`-style alphabets, accents, fractions and roots — and writes the same thing as
 * plain Unicode. It is a converter for short passages and formulas, not a TeX engine:
 * macros are not expanded, and a command with no Unicode form is left exactly as typed
 * and reported.
 */

import {
  ALPHABETS,
  FORWARD_SYMBOLS,
  MATH_ACCENT_MARKS,
  mapScript,
  NEGATIONS,
  STYLE_COMMANDS,
  SUBSCRIPTS,
  SUPERSCRIPT_PASSTHROUGH,
  SUPERSCRIPTS,
  TEXT_ACCENT_MARKS,
  TEXT_LETTER_COMMANDS,
  VULGAR_FRACTIONS,
} from "@/lib/tool-kit/latex-dev/unicode-maps";

export type ScriptFallback = "keep" | "parens";

export interface ToUnicodeOptions {
  /** Drop `$`, `$$`, `\(`, `\)`, `\[` and `\]` (the formula is then plain text). */
  stripMathDelimiters: boolean;
  /** What to write when a superscript or subscript has no Unicode form. */
  scriptFallback: ScriptFallback;
  /** Read `` `` ``, `''`, `--` and `---` as quotes and dashes. */
  ligatures: boolean;
  /** Remove `%` comments. */
  stripComments: boolean;
}

export const DEFAULT_TO_UNICODE_OPTIONS: ToUnicodeOptions = {
  stripMathDelimiters: true,
  scriptFallback: "keep",
  ligatures: true,
  stripComments: true,
};

export interface ToUnicodeResult {
  text: string;
  /** Commands with no Unicode form, left as typed (distinct, in order of appearance). */
  unconverted: string[];
  /** Superscripts and subscripts left as LaTeX because a character has no raised form. */
  scriptsKept: string[];
  /** Commands, scripts and accents that were converted. */
  converted: number;
}

interface Ctx {
  opts: ToUnicodeOptions;
  unconverted: Set<string>;
  scriptsKept: string[];
  converted: number;
}

interface Step {
  out: string;
  next: number;
  math?: boolean;
}

interface Arg {
  raw: string;
  next: number;
}

const RE_LETTER = /[A-Za-z]/;
const RE_UNICODE_LETTER = /^\p{L}$/u;
const RE_CONTROL_SEQUENCE = /\\(?:[A-Za-z]+|[^A-Za-z])/y;
const RE_WHITESPACE = /\s+/g;
const RE_SIMPLE_OPERAND = /^[\p{L}\p{N}\p{M}.′]+$/u;

const MATH_ENVIRONMENTS: ReadonlySet<string> = new Set([
  "equation",
  "equation*",
  "align",
  "align*",
  "gather",
  "gather*",
  "multline",
  "multline*",
  "eqnarray",
  "eqnarray*",
  "displaymath",
  "math",
  "split",
  "aligned",
  "gathered",
  "cases",
]);

/** Commands that only affect typesetting: dropped, together with a following space. */
const DROPPED: ReadonlySet<string> = new Set([
  "displaystyle",
  "textstyle",
  "scriptstyle",
  "scriptscriptstyle",
  "limits",
  "nolimits",
  "nonumber",
  "notag",
  "middle",
  "relax",
  "protect",
  "ignorespaces",
  "noindent",
  "centering",
]);

/** Commands whose braced argument is typesetting only. */
const DROPPED_WITH_ARGUMENT: ReadonlySet<string> = new Set([
  "label",
  "phantom",
  "hphantom",
  "vphantom",
  "hspace",
  "vspace",
]);

/** Sizing commands; the delimiter that follows them stays. */
const SIZING: ReadonlySet<string> = new Set([
  "left",
  "right",
  "bigl",
  "bigr",
  "Bigl",
  "Bigr",
  "biggl",
  "biggr",
  "Biggl",
  "Biggr",
  "big",
  "Big",
  "bigg",
  "Bigg",
]);

/** Commands that set their argument as ordinary text: only the markup goes. */
const TEXT_WRAPPERS: ReadonlySet<string> = new Set([
  "text",
  "textrm",
  "textsf",
  "texttt",
  "textbf",
  "textit",
  "textsl",
  "textsc",
  "textnormal",
  "textup",
  "emph",
  "mbox",
  "hbox",
  "mathrm",
  "mathnormal",
  "operatorname",
]);

/** Named functions, written upright in LaTeX. */
const FUNCTION_NAMES: ReadonlySet<string> = new Set([
  "sin",
  "cos",
  "tan",
  "cot",
  "sec",
  "csc",
  "arcsin",
  "arccos",
  "arctan",
  "sinh",
  "cosh",
  "tanh",
  "coth",
  "log",
  "ln",
  "lg",
  "exp",
  "lim",
  "limsup",
  "liminf",
  "max",
  "min",
  "sup",
  "inf",
  "arg",
  "det",
  "dim",
  "ker",
  "gcd",
  "deg",
  "hom",
  "Pr",
  "bmod",
]);

const SPACING_COMMANDS: Readonly<Record<string, string>> = {
  quad: " ",
  qquad: "  ",
};

const SPACING_SYMBOLS: Readonly<Record<string, string>> = {
  ",": " ",
  ":": " ",
  ";": " ",
  "!": "",
  " ": " ",
  "-": "",
  "/": "",
  "@": "",
};

const PRIME_RUNS = ["", "′", "″", "‴"];

/** Index of the brace closing the one at `open`, or -1. Escaped braces do not count. */
function findClose(s: string, open: number): number {
  let depth = 0;
  for (let i = open; i < s.length; i++) {
    const c = s[i];
    if (c === "\\") {
      i++;
    } else if (c === "{") {
      depth++;
    } else if (c === "}") {
      depth--;
      if (depth === 0) {
        return i;
      }
    }
  }
  return -1;
}

function skipSpaces(s: string, from: number): number {
  let p = from;
  while (p < s.length && (s[p] === " " || s[p] === "\n" || s[p] === "\t")) {
    p++;
  }
  return p;
}

/** One TeX argument: a braced group, a control sequence, or a single character. */
function readArg(s: string, from: number): Arg | null {
  const p = skipSpaces(s, from);
  if (p >= s.length) {
    return null;
  }
  if (s[p] === "{") {
    const close = findClose(s, p);
    if (close < 0) {
      return { raw: s.slice(p + 1), next: s.length };
    }
    return { raw: s.slice(p + 1, close), next: close + 1 };
  }
  if (s[p] === "\\") {
    RE_CONTROL_SEQUENCE.lastIndex = p;
    const match = RE_CONTROL_SEQUENCE.exec(s);
    if (match) {
      return { raw: match[0], next: p + match[0].length };
    }
  }
  const width = (s.codePointAt(p) ?? 0) > 0xff_ff ? 2 : 1;
  return { raw: s.slice(p, p + width), next: p + width };
}

/** The `[...]` optional argument starting at `from` (after spaces), if there is one. */
function readOptional(s: string, from: number): Arg | null {
  const p = skipSpaces(s, from);
  if (s[p] !== "[") {
    return null;
  }
  const close = s.indexOf("]", p);
  if (close < 0) {
    return null;
  }
  return { raw: s.slice(p + 1, close), next: close + 1 };
}

function convertArg(ctx: Ctx, raw: string, math: boolean): string {
  return convertRange(ctx, raw, math);
}

function note(ctx: Ctx, name: string): void {
  ctx.unconverted.add(name);
}

// ---------------------------------------------------------------------------
// Accents
// ---------------------------------------------------------------------------

function undotted(base: string): string {
  if (base === "ı") {
    return "i";
  }
  return base === "ȷ" ? "j" : base;
}

/** Put a combining mark on a character (or on every character), composing where Unicode can. */
function accentText(inner: string, mark: string, every: boolean): string {
  const chars = Array.from(inner.replace(RE_WHITESPACE, " "));
  if (chars.length === 0) {
    return mark;
  }
  if (every) {
    return chars
      .map((ch) =>
        ch === " " ? ch : `${undotted(ch)}${mark}`.normalize("NFC")
      )
      .join("");
  }
  const [first, ...rest] = chars;
  return `${`${undotted(first)}${mark}`.normalize("NFC")}${rest.join("")}`;
}

const MARKS_ON_EVERY_CHARACTER: ReadonlySet<string> = new Set(["̅", "̲", "⃗"]);

function mathAccent(ctx: Ctx, name: string, argRaw: string): string {
  const mark = MATH_ACCENT_MARKS[name];
  const inner = convertArg(ctx, argRaw, true);
  return accentText(inner, mark, MARKS_ON_EVERY_CHARACTER.has(mark));
}

function textAccentStep(
  ctx: Ctx,
  s: string,
  command: string,
  from: number,
  start: number
): Step {
  const arg = readArg(s, from);
  if (!arg) {
    note(ctx, `\\${command}`);
    return { out: s.slice(start, from), next: from };
  }
  const inner = convertArg(ctx, arg.raw, false);
  ctx.converted++;
  if (inner.trim() === "") {
    // \^{} and \~{} are how LaTeX types a literal caret and tilde.
    return { out: RE_LETTER.test(command) ? "" : command, next: arg.next };
  }
  if (command === "t") {
    const [a, b = "", ...rest] = Array.from(inner);
    return { out: `${a}͡${b}${rest.join("")}`, next: arg.next };
  }
  return {
    out: accentText(inner, TEXT_ACCENT_MARKS[command], false),
    next: arg.next,
  };
}

// ---------------------------------------------------------------------------
// Fractions, roots, styles
// ---------------------------------------------------------------------------

function wrapOperand(text: string): string {
  return RE_SIMPLE_OPERAND.test(text) ? text : `(${text})`;
}

function fractionStep(ctx: Ctx, s: string, from: number, start: number): Step {
  const top = readArg(s, from);
  const bottom = top ? readArg(s, top.next) : null;
  if (!(top && bottom)) {
    note(ctx, "\\frac");
    return { out: s.slice(start, from), next: from };
  }
  const a = convertArg(ctx, top.raw, true).trim();
  const b = convertArg(ctx, bottom.raw, true).trim();
  ctx.converted++;
  const vulgar = VULGAR_FRACTIONS[`${a}/${b}`];
  return {
    out: vulgar ?? `${wrapOperand(a)}/${wrapOperand(b)}`,
    next: bottom.next,
  };
}

const ROOT_SIGNS: Readonly<Record<string, string>> = { "3": "∛", "4": "∜" };

function rootStep(ctx: Ctx, s: string, from: number, start: number): Step {
  const index = readOptional(s, from);
  const arg = readArg(s, index ? index.next : from);
  if (!arg) {
    note(ctx, "\\sqrt");
    return { out: s.slice(start, from), next: from };
  }
  const radicand = convertArg(ctx, arg.raw, true).trim();
  const body = RE_SIMPLE_OPERAND.test(radicand) ? radicand : `(${radicand})`;
  let sign = "√";
  if (index) {
    const n = convertArg(ctx, index.raw, true).trim();
    const raised = mapScript(n, SUPERSCRIPTS);
    sign = ROOT_SIGNS[n] ?? (raised === null ? "" : `${raised}√`);
    if (sign === "") {
      ctx.scriptsKept.push(s.slice(start, arg.next));
      return { out: s.slice(start, arg.next), next: arg.next };
    }
  }
  ctx.converted++;
  return { out: `${sign}${body}`, next: arg.next };
}

function styleStep(
  ctx: Ctx,
  s: string,
  name: string,
  from: number,
  start: number
): Step {
  const arg = readArg(s, from);
  if (!arg) {
    note(ctx, `\\${name}`);
    return { out: s.slice(start, from), next: from };
  }
  const table = ALPHABETS[STYLE_COMMANDS[name]];
  const inner = convertArg(ctx, arg.raw, true);
  ctx.converted++;
  const out = Array.from(inner)
    .map((ch) => table.get(ch) ?? ch)
    .join("");
  return { out, next: arg.next };
}

function textWrapperStep(
  ctx: Ctx,
  s: string,
  name: string,
  from: number,
  start: number
): Step {
  const arg = readArg(s, from);
  if (!arg) {
    note(ctx, `\\${name}`);
    return { out: s.slice(start, from), next: from };
  }
  ctx.converted++;
  return { out: convertArg(ctx, arg.raw, false), next: arg.next };
}

function raisedTextStep(
  ctx: Ctx,
  s: string,
  name: string,
  from: number,
  start: number
): Step {
  const arg = readArg(s, from);
  if (!arg) {
    note(ctx, `\\${name}`);
    return { out: s.slice(start, from), next: from };
  }
  const inner = convertArg(ctx, arg.raw, false).replace(RE_WHITESPACE, "");
  const table = name === "textsuperscript" ? SUPERSCRIPTS : SUBSCRIPTS;
  const mapped = mapScript(inner, table);
  if (mapped === null) {
    ctx.scriptsKept.push(s.slice(start, arg.next));
    return { out: s.slice(start, arg.next), next: arg.next };
  }
  ctx.converted++;
  return { out: mapped, next: arg.next };
}

// ---------------------------------------------------------------------------
// Commands
// ---------------------------------------------------------------------------

function negationStep(ctx: Ctx, s: string, from: number, start: number): Step {
  const p = skipSpaces(s, from);
  let key = s[p] ?? "";
  let next = p + 1;
  if (key === "\\") {
    RE_CONTROL_SEQUENCE.lastIndex = p;
    const match = RE_CONTROL_SEQUENCE.exec(s);
    key = match ? match[0] : "";
    next = p + key.length;
  }
  const negated = NEGATIONS[key];
  if (negated) {
    ctx.converted++;
    return { out: negated, next };
  }
  const arg = readArg(s, from);
  if (arg) {
    const target = convertArg(ctx, arg.raw, true);
    ctx.converted++;
    return { out: `${target}̸`, next: arg.next };
  }
  note(ctx, "\\not");
  return { out: s.slice(start, from), next: from };
}

function environmentStep(
  ctx: Ctx,
  s: string,
  name: string,
  from: number,
  start: number
): Step {
  const arg = readArg(s, from);
  if (!arg) {
    note(ctx, `\\${name}`);
    return { out: s.slice(start, from), next: from };
  }
  if (MATH_ENVIRONMENTS.has(arg.raw)) {
    ctx.converted++;
    return { out: "", next: arg.next, math: name === "begin" };
  }
  note(ctx, `\\${name}{${arg.raw}}`);
  return { out: s.slice(start, arg.next), next: arg.next };
}

/** An unknown command is kept as typed, together with the arguments right after it. */
function unknownStep(ctx: Ctx, s: string, name: string, from: number): Step {
  note(ctx, `\\${name}`);
  // The star of \section* belongs to the command.
  let end = s[from] === "*" ? from + 1 : from;
  for (;;) {
    if (s[end] === "[") {
      const close = s.indexOf("]", end);
      if (close < 0) {
        break;
      }
      end = close + 1;
    } else if (s[end] === "{") {
      const close = findClose(s, end);
      end = close < 0 ? s.length : close + 1;
    } else {
      break;
    }
  }
  return { out: s.slice(from - name.length - 1, end), next: end };
}

function symbolStep(
  name: string,
  symbol: string,
  s: string,
  from: number
): Step {
  // TeX swallows the space after a control word, which is how "Stra\ss e" spells Straße.
  if (TEXT_LETTER_COMMANDS.has(name) && RE_UNICODE_LETTER.test(symbol)) {
    const p = skipSpaces(s, from);
    if (RE_LETTER.test(s[p] ?? "")) {
      return { out: symbol, next: p };
    }
  }
  return { out: symbol, next: from };
}

function sizingStep(s: string, from: number): Step {
  // \left. and \right. are invisible delimiters.
  return s[from] === "."
    ? { out: "", next: from + 1 }
    : { out: "", next: from };
}

function structuralStep(
  ctx: Ctx,
  s: string,
  name: string,
  from: number,
  start: number
): Step | null {
  if (name === "begin" || name === "end") {
    return environmentStep(ctx, s, name, from, start);
  }
  if (DROPPED.has(name)) {
    return { out: "", next: skipSpaces(s, from) };
  }
  if (DROPPED_WITH_ARGUMENT.has(name)) {
    const arg = readArg(s, from);
    return { out: "", next: arg ? arg.next : from };
  }
  if (SIZING.has(name)) {
    return sizingStep(s, from);
  }
  if (Object.hasOwn(SPACING_COMMANDS, name)) {
    return { out: SPACING_COMMANDS[name], next: from };
  }
  return null;
}

function operatorStep(
  ctx: Ctx,
  s: string,
  name: string,
  from: number,
  start: number
): Step | null {
  switch (name) {
    case "frac":
    case "dfrac":
    case "tfrac":
      return fractionStep(ctx, s, from, start);
    case "sqrt":
      return rootStep(ctx, s, from, start);
    case "not":
      return negationStep(ctx, s, from, start);
    case "textsuperscript":
    case "textsubscript":
      return raisedTextStep(ctx, s, name, from, start);
    case "pmod": {
      const arg = readArg(s, from);
      if (!arg) {
        return null;
      }
      ctx.converted++;
      return { out: `(mod ${convertArg(ctx, arg.raw, true)})`, next: arg.next };
    }
    default:
      return null;
  }
}

function commandStep(
  ctx: Ctx,
  s: string,
  name: string,
  from: number,
  start: number
): Step {
  const structural = structuralStep(ctx, s, name, from, start);
  if (structural) {
    return structural;
  }
  if (Object.hasOwn(STYLE_COMMANDS, name)) {
    return styleStep(ctx, s, name, from, start);
  }
  if (TEXT_WRAPPERS.has(name)) {
    return textWrapperStep(ctx, s, name, from, start);
  }
  if (Object.hasOwn(MATH_ACCENT_MARKS, name)) {
    const arg = readArg(s, from);
    if (arg) {
      ctx.converted++;
      return { out: mathAccent(ctx, name, arg.raw), next: arg.next };
    }
    note(ctx, `\\${name}`);
    return { out: s.slice(start, from), next: from };
  }
  const operator = operatorStep(ctx, s, name, from, start);
  if (operator) {
    return operator;
  }
  const symbol = FORWARD_SYMBOLS.get(`\\${name}`);
  if (symbol !== undefined) {
    ctx.converted++;
    return symbolStep(name, symbol, s, from);
  }
  if (FUNCTION_NAMES.has(name)) {
    ctx.converted++;
    // \sin\theta reads "sin θ": the command name ends where the next control word starts.
    const spaced = s[from] === "\\" ? " " : "";
    return { out: `${name === "bmod" ? "mod" : name}${spaced}`, next: from };
  }
  if (Object.hasOwn(TEXT_ACCENT_MARKS, name)) {
    return textAccentStep(ctx, s, name, from, start);
  }
  return unknownStep(ctx, s, name, from);
}

function controlSymbolStep(ctx: Ctx, s: string, i: number): Step {
  const c = s[i + 1];
  const next = i + 2;
  if (c === "(" || c === ")" || c === "[" || c === "]") {
    const opens = c === "(" || c === "[";
    return {
      out: ctx.opts.stripMathDelimiters ? "" : `\\${c}`,
      next,
      math: opens,
    };
  }
  if (c === "\\") {
    return { out: "\n", next };
  }
  if (Object.hasOwn(TEXT_ACCENT_MARKS, c)) {
    return textAccentStep(ctx, s, c, next, i);
  }
  const symbol = FORWARD_SYMBOLS.get(`\\${c}`);
  if (symbol !== undefined) {
    ctx.converted++;
    return { out: symbol, next };
  }
  if (Object.hasOwn(SPACING_SYMBOLS, c)) {
    return { out: SPACING_SYMBOLS[c], next };
  }
  // A control symbol with no Unicode reading keeps its backslash.
  note(ctx, `\\${c}`);
  return { out: `\\${c}`, next };
}

function backslashStep(ctx: Ctx, s: string, i: number): Step {
  const first = s[i + 1];
  if (first === undefined) {
    return { out: "\\", next: i + 1 };
  }
  if (!RE_LETTER.test(first)) {
    return controlSymbolStep(ctx, s, i);
  }
  let end = i + 2;
  while (end < s.length && RE_LETTER.test(s[end])) {
    end++;
  }
  const name = s.slice(i + 1, end);
  if (name === "operatorname" && s[end] === "*") {
    end++;
  }
  return commandStep(ctx, s, name, end, i);
}

// ---------------------------------------------------------------------------
// Scripts, quotes, dashes
// ---------------------------------------------------------------------------

function scriptStep(ctx: Ctx, s: string, i: number): Step {
  const mark = s[i];
  const arg = readArg(s, i + 1);
  if (!arg) {
    return { out: mark, next: i + 1 };
  }
  const inner = convertArg(ctx, arg.raw, true).replace(RE_WHITESPACE, "");
  const raised = mark === "^";
  const mapped = mapScript(
    inner,
    raised ? SUPERSCRIPTS : SUBSCRIPTS,
    raised ? SUPERSCRIPT_PASSTHROUGH : {}
  );
  if (mapped !== null) {
    ctx.converted++;
    return { out: mapped, next: arg.next };
  }
  const original = s.slice(i, arg.next);
  ctx.scriptsKept.push(original);
  if (ctx.opts.scriptFallback === "parens") {
    return { out: `${mark}(${inner})`, next: arg.next };
  }
  return { out: original, next: arg.next };
}

function hyphenStep(ctx: Ctx, s: string, i: number, math: boolean): Step {
  if (math) {
    return { out: "−", next: i + 1 };
  }
  if (!ctx.opts.ligatures) {
    return { out: "-", next: i + 1 };
  }
  if (s.startsWith("---", i)) {
    return { out: "—", next: i + 3 };
  }
  if (s.startsWith("--", i)) {
    return { out: "–", next: i + 2 };
  }
  return { out: "-", next: i + 1 };
}

function quoteStep(ctx: Ctx, s: string, i: number, math: boolean): Step {
  const c = s[i];
  if (c === "`") {
    if (!ctx.opts.ligatures || math) {
      return { out: c, next: i + 1 };
    }
    return s[i + 1] === "`"
      ? { out: "“", next: i + 2 }
      : { out: "‘", next: i + 1 };
  }
  if (math) {
    let run = 1;
    while (s[i + run] === "'" && run < 3) {
      run++;
    }
    return { out: PRIME_RUNS[run], next: i + run };
  }
  if (ctx.opts.ligatures && s[i + 1] === "'") {
    return { out: "”", next: i + 2 };
  }
  return { out: c, next: i + 1 };
}

function dollarStep(ctx: Ctx, s: string, i: number, math: boolean): Step {
  const width = s[i + 1] === "$" ? 2 : 1;
  return {
    out: ctx.opts.stripMathDelimiters ? "" : "$".repeat(width),
    next: i + width,
    math: !math,
  };
}

function commentStep(ctx: Ctx, s: string, i: number): Step {
  if (!ctx.opts.stripComments) {
    return { out: "%", next: i + 1 };
  }
  const end = s.indexOf("\n", i);
  return { out: "", next: end < 0 ? s.length : end };
}

function groupStep(ctx: Ctx, s: string, i: number, math: boolean): Step {
  const close = findClose(s, i);
  const inner = s.slice(i + 1, close < 0 ? s.length : close);
  return {
    out: convertRange(ctx, inner, math),
    next: close < 0 ? s.length : close + 1,
  };
}

function step(ctx: Ctx, s: string, i: number, math: boolean): Step {
  const c = s[i];
  switch (c) {
    case "\\":
      return backslashStep(ctx, s, i);
    case "{":
      return groupStep(ctx, s, i, math);
    case "}":
      return { out: "", next: i + 1 };
    case "^":
    case "_":
      return scriptStep(ctx, s, i);
    case "$":
      return dollarStep(ctx, s, i, math);
    case "%":
      return commentStep(ctx, s, i);
    case "~":
      return { out: "\u{A0}", next: i + 1 };
    case "-":
      return hyphenStep(ctx, s, i, math);
    case "`":
    case "'":
      return quoteStep(ctx, s, i, math);
    default:
      return { out: c, next: i + 1 };
  }
}

function convertRange(ctx: Ctx, s: string, startMath: boolean): string {
  let out = "";
  let math = startMath;
  let i = 0;
  while (i < s.length) {
    const result = step(ctx, s, i, math);
    out += result.out;
    i = result.next;
    if (result.math !== undefined) {
      math = result.math;
    }
  }
  return out;
}

/** Convert LaTeX to Unicode text. Pure and synchronous. */
export function latexToUnicode(
  source: string,
  options: ToUnicodeOptions = DEFAULT_TO_UNICODE_OPTIONS
): ToUnicodeResult {
  const ctx: Ctx = {
    opts: options,
    unconverted: new Set(),
    scriptsKept: [],
    converted: 0,
  };
  const text = convertRange(ctx, source, false);
  return {
    text,
    unconverted: [...ctx.unconverted],
    scriptsKept: ctx.scriptsKept,
    converted: ctx.converted,
  };
}
